import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Plus, Edit2, Trash2, Save, X, TrendingDown, FileText, Calendar } from 'lucide-react';
import { formatINR, stripCommas } from '../lib/numberFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ACCOUNT_TYPES = ['MTF', 'LAS', 'Loan'];

const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const todayStr = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

const emptyForm = () => ({
  account_type: 'MTF', account_code: '', bank_name: '',
  payment_date: todayStr(), interest_amount: '', principal_amount: '', notes: '',
});

const LoanPayments = ({ user, setUser, embedded = false }) => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [filterAccountType, setFilterAccountType] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterMonth) {
        params.from_date = `${filterMonth}-01`;
        params.to_date = `${filterMonth}-31`;
      }
      if (filterAccountType) params.account_type = filterAccountType;

      const [list, sum] = await Promise.all([
        api.get('/loan-payments', { params }),
        api.get('/loan-payments/summary', { params: filterMonth ? { month: filterMonth } : {} }),
      ]);
      setPayments(list.data || []);
      setSummary(sum.data);
    } catch (e) { toast.error('Failed to load payments'); }
  }, [filterMonth, filterAccountType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setShowDialog(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      account_type: p.account_type, account_code: p.account_code,
      bank_name: p.bank_name || '', payment_date: p.payment_date,
      interest_amount: p.interest_amount, principal_amount: p.principal_amount,
      notes: p.notes || '',
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.account_code.trim()) { toast.error('Account code required'); return; }
    if (!form.payment_date) { toast.error('Payment date required'); return; }
    const i = parseFloat(form.interest_amount) || 0;
    const p = parseFloat(form.principal_amount) || 0;
    if (i === 0 && p === 0) { toast.error('Enter interest or principal amount'); return; }

    try {
      setSaving(true);
      const payload = {
        ...form,
        interest_amount: i,
        principal_amount: p,
      };
      if (editingId) await api.put(`/loan-payments/${editingId}`, payload);
      else await api.post('/loan-payments', payload);
      toast.success(editingId ? 'Payment updated' : 'Payment recorded');
      setShowDialog(false);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await api.delete(`/loan-payments/${id}`);
      toast.success('Deleted');
      await fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const monthLabel = useMemo(() => {
    if (!filterMonth) return 'All time';
    const [y, m] = filterMonth.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  }, [filterMonth]);

  return (() => {
    const content = (
      <div data-testid="loan-payments-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Loan Payments Ledger</h1>
            <p className="text-sm text-gray-600">Record monthly interest + principal paid to LAS / MTF / Loan accounts</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input
              type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
              className="input-field text-sm" data-testid="filter-month"
            />
            <select value={filterAccountType} onChange={e => setFilterAccountType(e.target.value)} className="input-field text-sm" data-testid="filter-type">
              <option value="">All Types</option>
              {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Button onClick={openAdd} className="btn-primary text-sm flex items-center gap-1" data-testid="add-payment-btn">
              <Plus size={14} /> Add Payment
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="summary-cards">
            <div className="card p-3 bg-gradient-to-br from-amber-50 to-orange-50">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Interest ({monthLabel})</p>
              <p className="text-xl sm:text-2xl font-bold text-amber-700">{fmtINR(summary.totals.interest)}</p>
            </div>
            <div className="card p-3 bg-gradient-to-br from-blue-50 to-indigo-50">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Principal ({monthLabel})</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-700">{fmtINR(summary.totals.principal)}</p>
            </div>
            <div className="card p-3 bg-gradient-to-br from-rose-50 to-red-50">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Outflow</p>
              <p className="text-xl sm:text-2xl font-bold text-rose-700">{fmtINR(summary.totals.total)}</p>
            </div>
            <div className="card p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payments</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-700">{summary.totals.payments_count}</p>
            </div>
          </div>
        )}

        {/* By-account breakdown */}
        {summary && summary.by_account.length > 0 && (
          <div className="card p-4 mb-6" data-testid="by-account-card">
            <h3 className="font-semibold mb-3 text-gray-700">By Account ({monthLabel})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-gray-500 border-b">
                  <th className="text-left py-1.5 px-2 font-medium">Account</th>
                  <th className="text-left py-1.5 px-2 font-medium">Bank</th>
                  <th className="text-right py-1.5 px-2 font-medium">Interest</th>
                  <th className="text-right py-1.5 px-2 font-medium">Principal</th>
                  <th className="text-right py-1.5 px-2 font-medium">Total</th>
                  <th className="text-right py-1.5 px-2 font-medium"># Pmts</th>
                </tr></thead>
                <tbody>
                  {summary.by_account.map(a => (
                    <tr key={`${a.account_type}-${a.account_code}`} className="border-b last:border-0">
                      <td className="py-1.5 px-2"><span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800 mr-1">{a.account_type}</span> {a.account_code}</td>
                      <td className="py-1.5 px-2 text-gray-600">{a.bank_name || '—'}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{fmtINR(a.interest)}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{fmtINR(a.principal)}</td>
                      <td className="py-1.5 px-2 text-right font-bold text-rose-700">{fmtINR(a.total)}</td>
                      <td className="py-1.5 px-2 text-right text-gray-500">{a.payments_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments table */}
        <div className="card" data-testid="payments-table">
          <div className="p-3 border-b flex items-center gap-2">
            <FileText size={16} /> <h3 className="font-semibold">Payment Records ({payments.length})</h3>
          </div>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <TrendingDown size={32} className="mx-auto mb-2 opacity-50" />
              <p>No payments recorded for {monthLabel}.</p>
              <button onClick={openAdd} className="text-sm text-primary hover:underline mt-2">Add your first payment →</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold">Date</th>
                    <th className="text-left py-2 px-3 font-semibold">Type</th>
                    <th className="text-left py-2 px-3 font-semibold">Account</th>
                    <th className="text-left py-2 px-3 font-semibold">Bank</th>
                    <th className="text-right py-2 px-3 font-semibold">Interest</th>
                    <th className="text-right py-2 px-3 font-semibold">Principal</th>
                    <th className="text-right py-2 px-3 font-semibold">Total</th>
                    <th className="text-left py-2 px-3 font-semibold">Notes</th>
                    <th className="text-center py-2 px-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3"><Calendar size={12} className="inline mr-1 opacity-60" />{p.payment_date}</td>
                      <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{p.account_type}</span></td>
                      <td className="py-2 px-3 font-medium">{p.account_code}</td>
                      <td className="py-2 px-3 text-gray-600">{p.bank_name || '—'}</td>
                      <td className="py-2 px-3 text-right text-amber-700">{fmtINR(p.interest_amount)}</td>
                      <td className="py-2 px-3 text-right text-blue-700">{fmtINR(p.principal_amount)}</td>
                      <td className="py-2 px-3 text-right font-bold">{fmtINR(p.total_amount)}</td>
                      <td className="py-2 px-3 text-xs text-gray-500 max-w-xs truncate" title={p.notes}>{p.notes || ''}</td>
                      <td className="py-2 px-3 text-center">
                        <button onClick={() => openEdit(p)} className="p-1 hover:bg-blue-50 rounded text-blue-600" data-testid={`edit-${p.id}`}><Edit2 size={14} /></button>
                        <button onClick={() => remove(p.id)} className="p-1 hover:bg-red-50 rounded text-red-600 ml-1" data-testid={`del-${p.id}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md" data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Payment' : 'Record Loan Payment'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Account Type</label>
                  <select value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })} className="input-field w-full" data-testid="form-type">
                    {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Account Code *</label>
                  <input type="text" value={form.account_code} onChange={e => setForm({ ...form, account_code: e.target.value })} placeholder="e.g. VMR" className="input-field w-full" data-testid="form-code" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Bank / Lender</label>
                <input type="text" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. Bajaj Securities" className="input-field w-full" data-testid="form-bank" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Payment Date *</label>
                <input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} className="input-field w-full" data-testid="form-date" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Interest (₹)</label>
                  <input type="text" inputMode="decimal" value={formatINR(form.interest_amount)} onChange={e => setForm({ ...form, interest_amount: stripCommas(e.target.value) })} className="input-field w-full" data-testid="form-interest" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Principal (₹)</label>
                  <input type="text" inputMode="decimal" value={formatINR(form.principal_amount)} onChange={e => setForm({ ...form, principal_amount: stripCommas(e.target.value) })} className="input-field w-full" data-testid="form-principal" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="input-field w-full" rows={2} data-testid="form-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="flex items-center gap-1"><X size={14} /> Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1" data-testid="form-save"><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
    if (embedded) return content;
    return <Layout user={user} setUser={setUser}>{content}</Layout>;
  })();
};

export default LoanPayments;
